import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENProductDescription from "@tCentro/Components/CENProductDescription";
import { COLORS, NISCO, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    productSettings: getSettingsCollection(state).productSettings,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface NISProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
    productDescriptionWrapper?: InjectableCss;
}

export const nisProductInformationStyles: NISProductListProductInformationStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.base};
            > div[class^="GridOffset"] {
                div[class^="GridItemStyle"] {
                    padding-top: 2px !important;
                    padding-bottom: 2px !important;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                        padding-top: 6px;
                    `,
                    css`
                        padding-left: 0px;
                        padding-top: 6px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
                span {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
            `,
            typographyProps: {
                css: css`
                    width: 100%;
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    font-size: ${TYPOGRAPHY.size.base};
                    line-height: 20px;
                    font-family: ${NISCO.TYPOGRAPHY.fontFamily.base};
                    color: #000000 !important;

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-size: ${TYPOGRAPHY.size.base};
                            `,
                            css`
                                font-size: ${TYPOGRAPHY.size.base};
                            `,
                            null,
                            null,
                            null,
                        ])}
                `,
            },
        },
    },
    brandGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            // min-height: 52px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-height: auto;
                    `,
                    css`
                        min-height: auto;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    partNumbersGridItem: {
        width: 12,
        css: css`
            margin-top: 0 !important;
            margin-bottom: 4px !important;
            min-height: 26px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-height: auto;
                    `,
                    css`
                        min-height: auto;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    swatchGridItem: {
        width: 12,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    availabilityGridItem: {
        width: 12,
    },
    attributesGridItem: {
        width: 12,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    productDescriptionWrapper: {
        css: css`
            width: 100%;
            min-height: 180px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    brandStyles: {
        nameLink: {
            css: css`
                width: 100%;
                span {
                    color: #444444 !important;
                    font-weight: ${NISCO.TYPOGRAPHY.weight.mediumbold} !important;
                    font-size: ${TYPOGRAPHY.size.smaller};
                    line-height: 14px !important;
                    font-family: ${NISCO.TYPOGRAPHY.fontFamily.base} !important;
                }
            `,
        },
    },
    partNumbersStyles: {
        manufacturerItemLabelText: {
            css: css`
                font-family: ${NISCO.TYPOGRAPHY.fontFamily.base};
                font-size: ${TYPOGRAPHY.size.smaller};
                color: #333333;
                font-weight: ${TYPOGRAPHY.weight.base} !important;
                line-height: 14px;
            `,
        },
        manufacturerItemValueText: {
            css: css`
                line-height: 14px;
                font-family: ${NISCO.TYPOGRAPHY.fontFamily.base};
            `,
        },
    },
};

const styles = nisProductInformationStyles;

const NISProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
}: Props) => {
    const { product } = productContext;

    if (!product) {
        return null;
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    let variantDisplayTrait = null;

    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    return (
        <GridContainer {...styles.container}>
            {product.brand && showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            {showTitle && (
                <GridItem {...styles.descriptionGridItem}>
                    <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} />
                </GridItem>
            )}
            {showPartNumbers && (
                <GridItem {...styles.partNumbersGridItem}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.partNumbersStyles}
                    />
                </GridItem>
            )}
            {/* {variantDisplayTrait && (
                <GridItem {...styles.swatchGridItem}>
                    <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                </GridItem>
            )} */}
            {showTextBadges && textBadges && (
                <GridItem {...styles.textBadgeGridItem}>
                    <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                </GridItem>
            )}
            {showImageBadges && imageBadges && (
                <GridItem {...styles.imageBadgeGridItem}>
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                </GridItem>
            )}
            {/* {showAttributes && (
                <GridItem {...styles.attributesGridItem}>
                    <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                </GridItem>
            )} */}
            {/* Removed product description */}
            {/* <StyledWrapper {...styles.productDescriptionWrapper}>
                <CENProductDescription product={productContext} />
            </StyledWrapper> */}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(NISProductListProductInformation));
